import React from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { ChevronRight, CheckCircle2, Phone, ArrowRight, ArrowLeft } from "lucide-react";
import { SERVICES, GALLERY, COMPANY, FAQS } from "../mock";
import { SectionHeading, FAQAccordion } from "../components/common";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";
import { BeforeAfterSection } from "../components/BeforeAfterSlider";

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const idx = SERVICES.findIndex((s) => s.id === id);
  const service = SERVICES[idx];
  useScrollReveal([id]);
  useSEO({
    title: service ? `${service.title} in Melbourne, FL | Miller & Miller Services LLC` : "Services | Miller & Miller",
    description: service ? `${service.short} Custom ${service.title.toLowerCase()} by Miller & Miller Services LLC in Melbourne, FL. Licensed, insured & family-owned. Free estimates.` : "",
    path: service ? `/services/${service.id}` : "/services",
    image: service ? service.image : undefined,
  });

  if (!service) return <Navigate to="/services" replace />;

  const prev = SERVICES[(idx - 1 + SERVICES.length) % SERVICES.length];
  const next = SERVICES[(idx + 1) % SERVICES.length];
  const related = GALLERY.filter((g) => g.cat.toLowerCase().includes(service.title.split(" ")[0].toLowerCase())).slice(0, 4);
  const strip = (related.length ? related : GALLERY).slice(0, 4);

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-40 md:pb-24 bg-brand-navy text-white overflow-hidden">
        <img src={service.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/92 via-brand-navy/85 to-brand-ocean/50" />
        <div className="container-x relative">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/services" className="hover:text-white">Services</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-brand-gold2">{service.title}</span>
          </nav>
          {service.tag && (
            <span className="inline-block bg-brand-gold text-brand-navy text-xs font-heading font-extrabold uppercase tracking-wide px-3 py-1.5 rounded-full mb-4">
              {service.tag}
            </span>
          )}
          <h1 className="text-3xl md:text-5xl font-heading font-extrabold max-w-3xl">{service.title}</h1>
          <p className="mt-4 text-white/80 text-lg max-w-2xl">{service.short}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={() => navigate("/estimate")} className="h-12 px-7 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-bold shadow-gold">
              Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white/10 hover:bg-white/20 text-white font-heading font-bold ring-1 ring-white/25 transition-colors">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section-y bg-white">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal rounded-3xl overflow-hidden shadow-card">
            <img src={service.image} alt={service.title} className="w-full h-[420px] object-cover" />
          </div>
          <div className="reveal">
            <SectionHeading eyebrow="Overview" title={`Why Choose Our ${service.title}`} />
            <p className="mt-4 text-brand-slate leading-relaxed">{service.long}</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {service.features.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm font-semibold text-brand-navy">
                  <CheckCircle2 className="h-5 w-5 text-brand-ocean shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-7 rounded-2xl bg-brand-mist/60 p-5 flex items-center gap-4">
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-ocean text-white shrink-0"><CheckCircle2 className="h-6 w-6" /></span>
              <p className="text-sm text-brand-navy font-semibold">Licensed, insured & family-owned. Every {service.title.toLowerCase()} is custom-built and backed by our lifetime manufacturing warranty.</p>
            </div>
          </div>
        </div>
      </section>

      {service.id === "rescreen-repair" && <BeforeAfterSection className="pb-16 md:pb-24 bg-white" />}

      {/* Related gallery */}
      <section className="section-y bg-brand-mist/40">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <SectionHeading eyebrow="Recent Work" title="See It In Action" />
            <Link to="/gallery" className="reveal inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-ocean hover:bg-brand-sky text-white font-heading font-bold transition-colors shrink-0">
              View Full Gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {strip.map((g, i) => (
              <Link key={i} to="/gallery" className="reveal group overflow-hidden rounded-xl shadow-card" style={{ transitionDelay: `${i * 60}ms` }}>
                <img src={g.src} alt={g.title} loading="lazy" className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-white">
        <div className="container-x max-w-3xl">
          <SectionHeading center eyebrow="Questions?" title="Common Questions" />
          <div className="mt-10"><FAQAccordion faqs={FAQS} /></div>
        </div>
      </section>

      {/* Prev / Next + CTA */}
      <section className="bg-gradient-to-r from-brand-ocean to-brand-sky">
        <div className="container-x py-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
            <Link to={`/services/${prev.id}`} className="inline-flex items-center gap-2 text-white font-heading font-bold hover:gap-3 transition-all">
              <ArrowLeft className="h-4 w-4" /> {prev.title}
            </Link>
            <Link to={`/services/${next.id}`} className="inline-flex items-center gap-2 text-white font-heading font-bold hover:gap-3 transition-all">
              {next.title} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-white">Ready to get started?</h2>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate("/estimate")} className="h-12 px-8 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold">
                Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-brand-navy/30 hover:bg-brand-navy/50 text-white font-heading font-bold ring-1 ring-white/30 transition-colors">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
